import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, Pill } from "../../components/domain/Bits";
import { InteractionForm } from "../../components/domain/InteractionForm";
import { InteractionTimeline } from "../../components/domain/InteractionTimeline";
import { TaskPanels } from "../../components/domain/TaskPanels";
import { EventPanels } from "../../components/domain/EventPanels";
import Contact360Panel from "./Contact360Panel";
import ProfilingNotesCard from "./ProfilingNotesCard";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";
import { hasActionAccess, hasWorkspaceAccess } from "../../lib/workspaces";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building,
  Contact2,
  ExternalLink,
  KanbanSquare,
  Mail,
  MapPin,
  Phone,
  Plus,
  Pencil,
  ShieldCheck,
} from "lucide-react";

type LinkedOrg = {
  id: string;
  organization_id: string | null;
  organization_name: string | null;
  organization_archived: boolean;
  postal_code: string | null;
  postal_prefix: string | null;
  territory_id: string | null;
  territory_name: string | null;
  territory_status: string;
  territory_rep_user_id: string | null;
  territory_rep_name: string | null;
  relationship_type: string | null;
  title: string | null;
  department: string | null;
  is_primary: boolean;
  active: boolean;
  start_date: string | null;
  end_date: string | null;
};

type LinkedOpportunity = {
  id: string;
  name: string | null;
  status: string;
  archived: boolean;
  value: number;
  currency: string;
  pipeline_id: string | null;
  pipeline_label: string | null;
  current_stage_id: string | null;
  stage_label: string | null;
  canonical_stage: string | null;
  current_stage_entered_at: string | null;
  expected_close_date: string | null;
  won_at: string | null;
  lost_at: string | null;
};

type ContactDetailResponse = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  location: string | null;
  lifecycle_status: string;
  disposition: string | null;
  archived: boolean;
  external_id: string | null;
  source: string | null;
  tags?: string[];
  updated_at?: string;
  linked_organizations: LinkedOrg[];
  linked_opportunities: LinkedOpportunity[];
  owner: { id: string; name: string; email: string; active: boolean } | null;
  safe_provenance: {
    origin?: string;
    source_external_id?: string;
    source_created_at?: string;
    imported_at?: string;
  } | null;
};

type OrganizationChoice = { id: string; name: string; archived?: boolean; postal_code?: string; postal_prefix?: string; territory_name?: string; territory_status?: string; territory_id?: string };

function formatCurrency(n: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

const ORGANIZATION_TYPES = [
  { value: "medical_clinic", label: "Medical Clinic" },
  { value: "hospital", label: "Hospital" },
  { value: "independent_practice", label: "Independent Practice" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "rehabilitation_ltc", label: "Rehabilitation / Long-Term Care Facility" },
  { value: "community_health", label: "Community Health Organization" },
  { value: "diagnostic_sleep_clinic", label: "Diagnostic / Sleep Clinic" },
  { value: "other", label: "Other" },
];
export default function ContactDetail() {
  const { contactId = "" } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<ContactDetailResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [orgMode, setOrgMode] = useState<"existing" | "new">("existing");
  const [organizations, setOrganizations] = useState<OrganizationChoice[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [newOrganization, setNewOrganization] = useState({ name: "", org_type: "", email: "", phone: "", additional_phone: "", fax: "", website: "", address: "", postal_code: "" });
  const [territoryCheck, setTerritoryCheck] = useState<any>(null);
  const [linkingOrganization, setLinkingOrganization] = useState(false);
  const role = useEffectiveRole();
  const { user } = useAuth();
  const canLog = canAtLeast(role, "representative");
  const canEditContact = canLog && hasActionAccess(user, "contacts", "edit");
  const canAddAffiliation = canLog && hasActionAccess(user, "contacts", "add");
  const canDeactivateAffiliation = canLog && hasActionAccess(user, "contacts", "deactivate");
  const canViewRelationshipContacts = hasWorkspaceAccess(user, "relationship_contacts");

  const loadContact = useCallback(async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get<ContactDetailResponse>(`/crm/contacts/${contactId}`);
        setData(data);
      } catch (e: any) {
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          toastError(e);
        }
      } finally {
        setLoading(false);
      }
  }, [contactId]);

  useEffect(() => { loadContact(); }, [loadContact]);

  const openOrganizationDialog = async () => {
    setOrgDialogOpen(true);
    try {
      const { data: orgRows } = await api.get<OrganizationChoice[]>("/crm/organizations/link-options");
      const linkedIds = new Set((data?.linked_organizations || []).map((rel) => rel.organization_id));
      setOrganizations((orgRows || []).filter((row) => !row.archived && !linkedIds.has(row.id)));
    } catch (e) { toastError(e); }
  };

  const deactivateOrganization = async (relationship: LinkedOrg) => {
    if (!relationship.active) return;
    if (!window.confirm(`Deactivate ${relationship.organization_name || "this organization"} for this doctor? Historical referrals will remain available.`)) return;
    try {
      await api.delete(`/crm/relationships/${relationship.id}`);
      toast.success("Clinic affiliation deactivated; referral history preserved");
      await loadContact();
    } catch (e) { toastError(e); }
  };

  const validateNewOrganizationTerritory = async () => {
    if (newOrganization.postal_code.replace(/[^A-Za-z0-9]/g, "").length < 3) {
      setTerritoryCheck(null); return;
    }
    try {
      const { data: assessment } = await api.post("/tenant/territories/validate", {
        postal_code: newOrganization.postal_code,
      });
      setTerritoryCheck(assessment);
    } catch (e) { toastError(e); }
  };

  const linkOrganization = async () => {
    if (orgMode === "existing" && !organizationId) return;
    if (orgMode === "new" && !newOrganization.name.trim()) return;
    setLinkingOrganization(true);
    try {
      if (orgMode === "new") {
        let assessment = territoryCheck;
        if (!assessment) {
          const { data: validated } = await api.post("/tenant/territories/validate", {
            postal_code: newOrganization.postal_code,
          });
          assessment = validated;
          setTerritoryCheck(validated);
        }
        const submitForReview = assessment?.status === "outside_territory" || assessment?.status === "unassigned";
        const { data: result } = await api.post(
          `/crm/contacts/${contactId}/organizations/create-and-link`,
          {
            organization: {
              ...newOrganization,
              name: newOrganization.name.trim(),
              email: newOrganization.email.trim() || null,
              submit_for_territory_review: submitForReview,
            },
            relationship_type: "referral_source",
            is_primary: data?.linked_organizations.length === 0,
          },
        );
        if (result.status === "pending_review") {
          toast.success("Clinic submitted for territory review. It can be linked after approval.");
        } else {
          toast.success("Organization created and linked");
        }
      } else {
        await api.post("/crm/relationships", {
          contact_id: contactId,
          organization_id: organizationId,
          relationship_type: "referral_source",
          is_primary: data?.linked_organizations.length === 0,
        });
        toast.success("Organization linked");
      }
      setOrgDialogOpen(false);
      setOrganizationId("");
      setTerritoryCheck(null);
      setNewOrganization({ name: "", org_type: "", email: "", phone: "", additional_phone: "", fax: "", website: "", address: "", postal_code: "" });
      await loadContact();
    } catch (e) {
      toastError(e);
    } finally {
      setLinkingOrganization(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="contact-detail-loading">
        Loading contact…
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <>
        <PageHeader
          title="Contact not found"
          subtitle="This record may have been archived or belongs to another tenant."
          actions={
            <>
              {canViewRelationshipContacts && <Button variant="outline" onClick={() => nav(`/relationship-contacts?contact_id=${data.id}`)} data-testid="contact-detail-contact-persons"><Contact2 className="w-4 h-4 mr-1" /> Contact persons</Button>}
              <Button variant="outline" onClick={() => nav("/contacts")} data-testid="contact-detail-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to contacts
              </Button>
            </>
          }
        />
        <div className="max-w-4xl mx-auto p-6 md:p-8 text-sm text-slate-500" data-testid="contact-detail-not-found">
          We couldn’t find contact <span className="font-mono">{contactId}</span>.
        </div>
      </>
    );
  }

  const fullName = `${data.first_name} ${data.last_name || ""}`.trim() || "Unnamed contact";
  const openOpps = data.linked_opportunities.filter((o) => o.status === "open" && !o.archived);
  const closedOpps = data.linked_opportunities.filter((o) => o.status !== "open" || o.archived);

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Contact2 className="w-6 h-6 text-slate-500" />
            {fullName}
            {data.archived && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-widest" data-testid="contact-detail-archived-badge">
                Archived
              </span>
            )}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Pill value={data.lifecycle_status} type="lifecycle" testId="contact-detail-lifecycle" />
            {data.source && <span className="text-xs text-slate-500">Source: {data.source}</span>}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canEditContact && (
              <Button variant="outline" onClick={() => window.dispatchEvent(new CustomEvent("rk:edit-contact"))} data-testid="contact-detail-edit">
                <Pencil className="w-4 h-4 mr-1" /> Edit contact
              </Button>
            )}
            {canLog && (
              <Button
                onClick={() => setLogOpen(true)}
                data-testid="contact-detail-log-interaction"
                aria-label="Log an Interaction against this Contact"
              >
                <Plus className="w-4 h-4 mr-1" /> Log Interaction
              </Button>
            )}
            <Button variant="outline" onClick={() => nav("/contacts")} data-testid="contact-detail-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        }
      />
      <div className="max-w-6xl mx-auto p-6 md:p-8 grid gap-6 md:grid-cols-3" data-testid="contact-detail">
        {/* Sidebar summary */}
        <aside className="md:col-span-1 space-y-4">
          <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="contact-detail-summary">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Contact info
            </h2>
            <dl className="space-y-2.5 text-sm">
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Email</dt>
                  <dd className="break-all font-medium" data-testid="contact-detail-email">
                    {data.email || <span className="text-slate-400">—</span>}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Phone</dt>
                  <dd className="font-medium" data-testid="contact-detail-phone">
                    {data.phone || <span className="text-slate-400">—</span>}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Location</dt>
                  <dd className="font-medium" data-testid="contact-detail-location">
                    {data.location || <span className="text-slate-400">—</span>}
                  </dd>
                </div>
              </div>
              {data.owner && (
                <div className="flex items-start gap-2" data-testid="contact-detail-owner">
                  <ShieldCheck className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <dt className="text-xs text-slate-500">Owner</dt>
                    <dd className="font-medium">
                      {data.owner.name}
                      <span className="block text-xs text-slate-500 break-all">{data.owner.email}</span>
                    </dd>
                  </div>
                </div>
              )}
              {data.external_id && (
                <div className="text-xs text-slate-500 pt-2 border-t border-slate-100" data-testid="contact-detail-external-id">
                  External ID: <span className="font-mono text-slate-700">{data.external_id}</span>
                </div>
              )}
            </dl>
          </section>
          <ProfilingNotesCard contactId={contactId} />
          {data.safe_provenance && (
            <section
              className="border border-slate-200 rounded-lg bg-slate-50/60 p-4 text-xs text-slate-600"
              data-testid="contact-detail-provenance"
            >
              <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Import provenance
              </h2>
              <div className="space-y-1">
                <div><span className="text-slate-500">Origin: </span>{data.safe_provenance.origin || "—"}</div>
                <div><span className="text-slate-500">Source ID: </span>{data.safe_provenance.source_external_id || "—"}</div>
                <div><span className="text-slate-500">Source created: </span>{formatDate(data.safe_provenance.source_created_at)}</div>
                <div><span className="text-slate-500">Imported: </span>{formatDate(data.safe_provenance.imported_at)}</div>
              </div>
            </section>
          )}
        </aside>

        {/* Main column */}
        <div className="md:col-span-2 space-y-6">
          {/* Linked organizations */}
          <section className="border border-slate-200 rounded-lg bg-white" data-testid="contact-detail-orgs">
            <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Building className="w-4 h-4 text-slate-500" /> Organizations
                <span className="text-xs text-slate-400 font-normal">({data.linked_organizations.length})</span>
              </h2>
              {canAddAffiliation && <Button size="sm" variant="outline" onClick={openOrganizationDialog} data-testid="contact-link-organization"><Plus className="w-4 h-4 mr-1" /> Link organization</Button>}
            </header>
            {data.linked_organizations.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500" data-testid="contact-detail-orgs-empty">
                No organizations linked yet.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.linked_organizations.map((rel) => (
                  <li key={rel.id} className="px-4 py-3 flex items-start gap-3" data-testid={`contact-detail-org-${rel.id.slice(0, 8)}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {rel.organization_name || <span className="text-slate-400 italic">Unnamed organization</span>}
                        {rel.is_primary && (
                          <span className="ml-2 text-xs uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" data-testid={`contact-detail-org-primary-${rel.id.slice(0, 8)}`}>
                            Primary
                          </span>
                        )}
                        {rel.organization_archived && (
                          <span className="ml-2 text-xs uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>Type: <span className="font-mono text-slate-700">{rel.relationship_type || "—"}</span></span>
                        {rel.title && <span>Title: {rel.title}</span>}
                        {rel.department && <span>Dept: {rel.department}</span>}
                        {(rel.start_date || rel.end_date) && (
                          <span>
                            {formatDate(rel.start_date)}
                            {rel.end_date ? ` → ${formatDate(rel.end_date)}` : ""}
                          </span>
                        )}
                        <span>Territory: <strong className="text-slate-700">{rel.territory_name || (rel.territory_status === "unassigned" ? "Unassigned" : "—")}</strong></span>
                        {rel.postal_code && <span>Postal code: {rel.postal_code}</span>}
                        {rel.territory_rep_name && <span>Assigned rep: <strong className="text-slate-700">{rel.territory_rep_name}</strong></span>}
                        {!rel.active && <span className="text-slate-400">Inactive · history preserved</span>}
                      </div>
                    </div>
                    {canDeactivateAffiliation && rel.active && (
                      <Button variant="outline" size="sm" onClick={() => deactivateOrganization(rel)} data-testid={`deactivate-affiliation-${rel.id.slice(0, 8)}`}>
                        Deactivate
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
            <DialogContent data-testid="contact-organization-dialog">
              <DialogHeader><DialogTitle>Link organization</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Action</Label><Select value={orgMode} onValueChange={(value) => setOrgMode(value as "existing" | "new")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="existing">Link existing organization</SelectItem><SelectItem value="new">Create and link organization</SelectItem></SelectContent></Select></div>
                {orgMode === "existing" ? (
                  <div><Label>Organization</Label><Select value={organizationId} onValueChange={setOrganizationId}><SelectTrigger data-testid="existing-organization-select"><SelectValue placeholder="Choose organization" /></SelectTrigger><SelectContent>{organizations.map((org) => <SelectItem key={org.id} value={org.id}>{org.name}{org.territory_name ? ` · ${org.territory_name}` : ""}{org.postal_prefix ? ` · ${org.postal_prefix}` : ""}</SelectItem>)}</SelectContent></Select></div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label htmlFor="new-organization-name">Organization name</Label><Input id="new-organization-name" value={newOrganization.name} onChange={(event) => setNewOrganization({ ...newOrganization, name: event.target.value })} data-testid="new-organization-name" /></div>
                    <div><Label>Organization type (optional)</Label><Select value={newOrganization.org_type || "none"} onValueChange={(value) => setNewOrganization({ ...newOrganization, org_type: value === "none" ? "" : value })}><SelectTrigger data-testid="new-organization-type"><SelectValue placeholder="Select organization type" /></SelectTrigger><SelectContent><SelectItem value="none">— Not specified —</SelectItem>{ORGANIZATION_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Email</Label><Input type="email" value={newOrganization.email} onChange={(event) => setNewOrganization({ ...newOrganization, email: event.target.value })} data-testid="new-organization-email" /></div>
                    <div><Label>Primary phone</Label><Input value={newOrganization.phone} onChange={(event) => setNewOrganization({ ...newOrganization, phone: event.target.value })} data-testid="new-organization-phone" /></div>
                    <div><Label>Additional phone</Label><Input value={newOrganization.additional_phone} onChange={(event) => setNewOrganization({ ...newOrganization, additional_phone: event.target.value })} data-testid="new-organization-additional-phone" /></div>
                    <div><Label>Fax number</Label><Input value={newOrganization.fax} onChange={(event) => setNewOrganization({ ...newOrganization, fax: event.target.value })} data-testid="new-organization-fax" /></div>
                    <div className="col-span-2"><Label>Website</Label><Input value={newOrganization.website} onChange={(event) => setNewOrganization({ ...newOrganization, website: event.target.value })} placeholder="www.example.ca" data-testid="new-organization-website" /><p className="mt-1 text-xs text-slate-500">Example: www.example.com — https:// will be added automatically.</p></div>
                    <div className="col-span-2"><Label>Physical address</Label><Input value={newOrganization.address} onChange={(event) => setNewOrganization({ ...newOrganization, address: event.target.value })} data-testid="new-organization-address" /></div>
                    <div className="col-span-2"><Label>Canadian postal code</Label><Input value={newOrganization.postal_code} onChange={(event) => { setNewOrganization({ ...newOrganization, postal_code: event.target.value.toUpperCase() }); setTerritoryCheck(null); }} onBlur={validateNewOrganizationTerritory} placeholder="T5A 0A1" data-testid="new-organization-postal-code" /></div>
                    {territoryCheck?.status === "verified" && <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">Territory verified: {territoryCheck.territory?.name}{territoryCheck.territory?.primary_rep_name ? ` — ${territoryCheck.territory.primary_rep_name}` : ""}.</div>}
                    {territoryCheck?.status === "outside_territory" && <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"><strong>This clinic is outside your assigned territory.</strong><div className="mt-1">{territoryCheck.territory?.name || territoryCheck.postal_prefix}{territoryCheck.territory?.primary_rep_name ? ` is assigned to ${territoryCheck.territory.primary_rep_name}` : ""}. It can be submitted for review but will not be linked yet.</div></div>}
                    {territoryCheck?.status === "unassigned" && <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">No territory is configured for {territoryCheck.postal_prefix}. Submit this clinic for review.</div>}
                  </div>
                )}
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOrgDialogOpen(false)}>Cancel</Button><Button onClick={linkOrganization} disabled={linkingOrganization || (orgMode === "existing" ? !organizationId : !newOrganization.name.trim() || !newOrganization.address.trim() || !newOrganization.postal_code.trim())}>{linkingOrganization ? "Saving…" : ((territoryCheck?.status === "outside_territory" || territoryCheck?.status === "unassigned") ? "Submit for territory review" : "Save link")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Contact360Panel contactId={data.id} />

          {/* Linked opportunities */}
          <section className="border border-slate-200 rounded-lg bg-white" data-testid="contact-detail-opps">
            <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <KanbanSquare className="w-4 h-4 text-slate-500" /> Opportunities
                <span className="text-xs text-slate-400 font-normal">({data.linked_opportunities.length})</span>
              </h2>
            </header>
            {data.linked_opportunities.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500" data-testid="contact-detail-opps-empty">
                No opportunities yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {openOpps.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {openOpps.map((o) => (
                      <OpportunityRow key={o.id} opp={o} />
                    ))}
                  </ul>
                )}
                {closedOpps.length > 0 && (
                  <div className="px-4 py-2 text-xs uppercase tracking-widest text-slate-400">
                    Closed / archived ({closedOpps.length})
                  </div>
                )}
                {closedOpps.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {closedOpps.map((o) => (
                      <OpportunityRow key={o.id} opp={o} muted />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>

          {/* Activity — Contact-linked Interactions only. Per Slice 1B rules,
              we do NOT yet merge linked-Opportunity stage transitions here;
              that belongs to Client 360 v0. */}
          <section className="border border-slate-200 rounded-lg bg-white" data-testid="contact-detail-activity">
            <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Activity</h2>
            </header>
            <InteractionTimeline
              scope={{ contact_id: data.id }}
              reloadKey={timelineKey}
              onChanged={() => setTimelineKey((k) => k + 1)}
              emptyLabel="No interactions logged yet."
            />
          </section>

          {/* Slice 2A.1 — Tasks & Follow-Ups for this Contact. */}
          <TaskPanels scope={{ contact_id: data.id }} />
          <EventPanels scope={{ contact_id: data.id }} />
        </div>
      </div>
      <InteractionForm
        open={logOpen}
        onOpenChange={setLogOpen}
        onSuccess={() => setTimelineKey((k) => k + 1)}
        scope={{ contact_id: data.id }}
      />
    </>
  );
}

function OpportunityRow({ opp, muted = false }: { opp: LinkedOpportunity; muted?: boolean }) {
  return (
    <li className={`px-4 py-3 flex items-start gap-3 ${muted ? "opacity-70" : ""}`} data-testid={`contact-detail-opp-${opp.id.slice(0, 8)}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">
          {opp.name || <span className="text-slate-400 italic">Unnamed</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {opp.canonical_stage && <Pill value={opp.canonical_stage} type="stage" testId={`contact-detail-opp-stage-${opp.id.slice(0, 8)}`} />}
          <span>{formatCurrency(opp.value, opp.currency)}</span>
          {opp.pipeline_label && <span>Pipeline: {opp.pipeline_label}</span>}
          {opp.expected_close_date && <span>Expected: {formatDate(opp.expected_close_date)}</span>}
          {opp.won_at && <span className="text-emerald-700">Won: {formatDate(opp.won_at)}</span>}
          {opp.lost_at && <span className="text-rose-700">Lost: {formatDate(opp.lost_at)}</span>}
        </div>
      </div>
      <Link
        to={`/opportunities/${opp.id}`}
        className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 shrink-0 mt-0.5 focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
        data-testid={`contact-detail-opp-open-${opp.id.slice(0, 8)}`}
        aria-label={`Open opportunity ${opp.name || opp.id}`}
      >
        Open <ExternalLink className="w-3 h-3" />
      </Link>
    </li>
  );
}
